import { DEVICE_WIDTH } from '../../../utils/viewport-width';
import gsap from 'gsap';
import axios, { AxiosRequestConfig } from 'axios';
import { openDynamicBanner } from '@/components/dynamic-banner';
import { ease1 } from '@/animations/easings';
import PageHandlersService from '@/utils/page-handlers-service';
import createValidator from '@/modules/validator';
import { formatPrice } from '../../../utils/formatPrice';

import ajaxForms from '@/inits/ajax-forms';

const pageHandlers = new PageHandlersService();
const axiosAbortController = new AbortController();
const customEventsMap = new Map<number, Event>();
const productHandlers = new PageHandlersService();
const confirmForm = document.querySelector('.js-o-confirm-modal-container') as HTMLFormElement;
let controller = false;

let reconfirmNumberAction: string;
let retryTimeout: NodeJS.Timeout | null = null;
let timerEl: HTMLElement | null;
let intervalId: NodeJS.Timeout;

interface IProductAdditionResponse {
    success: boolean;
    message: string;
    total: number;
    bonusesWrittenOff: number;
    bonusesAccrued: string;
    totalDiscount: string;
    products: {
        id: number;
        previousPrice: string;
        price: string;
    }[];
}

interface IGetCartResponse {
    success: boolean;
    data: {
        contentHtml: string;
        totalPrice: number;
        totalCount: number;
    };
    message: string;
}

let errorMessage = '';
let errorEmptyCart = '';
const htmlElement = document.querySelector('html');
const langValue = htmlElement?.lang;

if (langValue === 'ru') {
    errorMessage = 'Произошла ошибка при обновлении товаров. Попробуйте через пару минут.';
    errorEmptyCart = 'Ваша корзина пуста. для добавления товаров перейдите в&nbsp;каталог';
} else {
    errorMessage = 'An error occurred while updating the products. Please try again in a few minutes.';
    errorEmptyCart = 'Your cart is empty. To add products, go to the catalog';
}

const init = () => {
    // Mobile content collapse
    if (!DEVICE_WIDTH.isDesktop) {
        const mobileLabel = document.querySelector('.js-o-content-mobile-label');
        const labelShowText = document.querySelector('.js-o-content-mobile-label-text-show');
        const labelHideText = document.querySelector('.js-o-content-mobile-label-text-hide');
        const labelOtherText = document.querySelector('.js-o-content-mobile-label-text-other');
        const orderContentCollapse = document.querySelector('.js-o-content-collapse');

        let isContentCollapseActive = false;

        if (mobileLabel && labelShowText && labelHideText && labelOtherText && orderContentCollapse) {
            const showAndHideTextsWidthDifference = labelHideText.clientWidth - labelShowText.clientWidth;

            const handleLabelClick = () => {
                if (isContentCollapseActive) {
                    gsap.set(orderContentCollapse, { height: 0 });
                    gsap.set(labelOtherText, { x: 0 });
                } else {
                    gsap.set(orderContentCollapse, { height: orderContentCollapse.scrollHeight });
                    gsap.set(labelOtherText, { x: showAndHideTextsWidthDifference });
                }

                mobileLabel.classList.toggle('active');

                isContentCollapseActive = !isContentCollapseActive;
            };
            handleLabelClick();
            pageHandlers.set(mobileLabel, handleLabelClick);
        }
    }

    function timerStart(eventFunction: EventListener, container: HTMLElement) {
        let seconds = 60;
        timerEl = container.querySelector('.js-code-timer-order');
        const updateTimer = () => {
            const remainingSeconds = seconds % 60;

            const displayText = `Получить повторный код через 00:${String(remainingSeconds).padStart(2, '0')}`;
            if (timerEl) {
                timerEl.removeEventListener('click', eventFunction);
                timerEl.textContent = displayText;

                if (seconds === 0) {
                    clearInterval(intervalId);
                    timerEl.textContent = 'Получить повторный код';
                    timerEl.classList.add('link', 'link--underlined');
                    timerEl.addEventListener('click', eventFunction);
                    controller = true;
                }
            }

            seconds--;
        };

        updateTimer();

        intervalId = setInterval(updateTimer, 1000);
    }

    // Enter Modal
    const enterButton = document.querySelector('.js-o-enter-btn');

    const enterModal = document.querySelector('.js-o-modal');
    const modalCloseElements = document.querySelectorAll('.js-o-close');
    const headerPopupsModal = document.querySelector('.js-hp-modal');

    const handleAccountClick = () => {
        if (headerPopupsModal) {
            headerPopupsModal.classList.add('active');

            const popupsInteractiveElements = Array.from(
                headerPopupsModal.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input'),
            );

            popupsInteractiveElements.forEach((element) => {
                element.tabIndex = 0;
            });
        }
    };

    pageHandlers.set(enterButton, handleAccountClick);

    modalCloseElements.forEach((element) => {
        const handleModalClose = () => {
            enterModal?.classList.remove('active');
        };

        pageHandlers.set(element, handleModalClose);
    });

    const infoBlockElement = document.querySelector('.js-p-info');

    const tooltipElement = document.querySelector<HTMLElement>('.js-p-info-tooltip-element');
    const tooltipContent = document.querySelector<HTMLElement>('.js-p-info-tooltip-content');

    let isTooltipOpened = false;

    const openTooltip = () => {
        tooltipContent?.classList.add('active');
    };

    const closeTooltip = () => {
        tooltipContent?.classList.remove('active');
    };

    if (DEVICE_WIDTH.isDesktop) {
        pageHandlers.set(tooltipElement, openTooltip, 'mouseenter');
        pageHandlers.set(tooltipElement, closeTooltip, 'mouseleave');
    } else {
        const handleTooltipElementClick = (event: Event) => {
            event.stopPropagation();

            if (isTooltipOpened) {
                closeTooltip();
            } else {
                openTooltip();
            }

            isTooltipOpened = !isTooltipOpened;
        };

        tooltipElement?.addEventListener('click', handleTooltipElementClick);
        document.body.addEventListener('click', closeTooltip);

        pageHandlers.set(tooltipElement, handleTooltipElementClick);
        pageHandlers.set(document.body, closeTooltip);
    }

    // Phone confirm Modal
    const personalInfoBlock = document.querySelector('.js-o-personal-info-block');

    const phoneConfirmButton = document.querySelector('.js-o-confirm-btn');
    const phoneNumberInput = document.querySelector<HTMLInputElement>('.js-intl-phone-order');
    const phoneConfirmModal = document.querySelector('.js-o-confirm-modal') as HTMLFormElement;
    const phoneConfirmModalClose = document.querySelector('.js-o-confirm-close');
    const phoneNumberInner = document.querySelector('.js-ps-change-phone-new-value');

    const authForm = document.querySelector('.js-o-confirm-modal-container');

    const emailInputGroup = personalInfoBlock?.querySelector('.js-email-input-group');

    const emailInput = emailInputGroup?.querySelector<HTMLInputElement>('.js-input');
    const nameInput = document.querySelector('.js-order-form-name');

    let hideTimeout: NodeJS.Timeout;

    const handleConfirmButtonClick = () => {
        confirmNumber('startAuth', authForm);
    };

    const handlePhoneConfirmModalClose = () => {
        phoneConfirmModal?.classList.remove('active');
    };

    pageHandlers.set(phoneConfirmModalClose, handlePhoneConfirmModalClose);
    pageHandlers.set(phoneConfirmButton, () => {
        const phoneField = document.querySelector('.js-ps-phone-field');
        const validator = createValidator(phoneField as HTMLFormElement, {
            scrollToInvalidInputOptions: {
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            },
        });
        const isFormValid = validator.validate();
        if (isFormValid) {
            handleConfirmButtonClick();
        }
    });

    function confirmNumber(action: string, form: HTMLFormElement) {
        const validator = createValidator(form, {
            scrollToInvalidInputOptions: {
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            },
        });
        const phoneNumber = phoneNumberInput?.value;
        const email = emailInput?.value;
        const name = nameInput?.value;

        let requestBody: AxiosRequestConfig<any> | undefined;

        if (action === 'register') {
            requestBody = {
                params: {
                    params: {
                        phone: phoneNumber,
                        action: action,
                        email: email,
                        name: name,
                    },
                },
            };
        } else if (action === 'startAuth') {
            requestBody = {
                params: {
                    params: {
                        phone: phoneNumber,
                        action: action,
                        email: email,
                    },
                },
            };
        }

        const userData = {
            userPhone: phoneNumber,
            userEmail: email,
            userName: name,
        };

        if (phoneNumber) {
            const isFormValid = validator.validate();
            if (isFormValid) {
                axios
                    .get('/local/ajax/mobileId.php', requestBody)
                    .then((response) => {
                        phoneConfirmModal?.classList.add('active');
                        const displayText = `Мы отправили вам пуш-уведомление на номер ${phoneNumber}.
                        Подтвердите (примите) его пожалуйста. Либо перейдите по ссылке в SMS для подтверждения действия.`;
                        if (phoneNumberInner) {
                            phoneNumberInner.innerHTML = displayText;
                        }

                        if (response.data.success && response.data.data.action) {
                            ajaxForms.showFailureMessage(form, response.data.message);

                            timerStart(reconfirmNumber, confirmForm);
                            numberRegistration(userData, response.data.data.action, response.data.data.method);

                            if (action === 'register') {
                                reconfirmNumberAction = 'register';
                            } else if (action === 'startAuth') {
                                reconfirmNumberAction = 'startAuth';
                            }
                        } else {
                            clearTimeout(hideTimeout);
                            if (!response.data.success) {
                                ajaxForms.showFormMessages(form);
                                hideTimeout = setTimeout(() => ajaxForms.hideFormMessages(form), 5000);
                                ajaxForms.showFailureMessage(form, response.data.message);
                                reconfirmNumberAction = 'register';
                                confirmNumber('register', form);
                            } else {
                                ajaxForms.showFormMessages(form);
                                hideTimeout = setTimeout(() => ajaxForms.hideFormMessages(form), 5000);
                                ajaxForms.showFailureMessage(form, response.data.message);
                                reconfirmNumberAction = 'startAuth';
                            }
                        }
                    })
                    .catch((error) => {
                        console.error('Ошибка:', error);
                        openDynamicBanner(error.message);
                    });
            }
        }
    }

    function reconfirmNumber() {
        const phoneNumber = phoneNumberInput?.value;
        const email = emailInput?.value;
        const name = nameInput?.value;

        const userData = {
            userPhone: phoneNumber,
            userEmail: email,
            userName: name,
        };

        const requestBody = {
            params: {
                params: {
                    phone: phoneNumber,
                    action: reconfirmNumberAction,
                },
            },
        };

        axios
            .get('/local/ajax/mobileId.php', requestBody)
            .then((response) => {
                clearTimeout(hideTimeout);
                ajaxForms.showFormMessages(confirmForm);
                if (response.data.success && response.data.data.action && phoneNumber) {
                    controller = false;
                    numberRegistration(userData, response.data.data.action, response.data.data.method);
                    timerStart(reconfirmNumber, confirmForm);
                }
            })
            .catch((error) => {
                console.error('Ошибка:', error);
                openDynamicBanner(error.message);
            });
    }

    function numberRegistration(user: object, action: string, method: string) {
        const requestBody = {
            params: {
                params: {
                    phone: user.userPhone,
                    action: action,
                    method: method,
                    name: user.userName,
                    email: user.userEmail,
                },
            },
        };

        if (retryTimeout) {
            clearTimeout(retryTimeout);
        }

        axios
            .get('/local/ajax/mobileId.php', requestBody)
            .then((response) => {
                clearTimeout(hideTimeout);
                ajaxForms.showFormMessages(confirmForm);
                if (response.data.success) {
                    ajaxForms.showSuccessMessage(confirmForm, response.data.message);
                    window.location.href = '/personal/order/make/';
                } else if (controller === false) {
                    ajaxForms.showFailureMessage(confirmForm, response.data.message);
                    retryTimeout = setTimeout(() => {
                        numberRegistration(user, action, method);
                    }, 5000);
                }
            })
            .catch((error) => {
                console.error('Ошибка:', error);
                ajaxForms.showFailureMessage(confirmForm, 'Произошла ошибка при отправке формы');
            });
    }

    function login(action: string) {
        const newPhoneNumber = phoneNumberInput?.value;
        const phoneNumberString = collector.getCode('.js-ps-change-phone-code-input');
        const email = emailInput?.value;
        const requestBody = {
            params: {
                params: {
                    action: action,
                    phone: newPhoneNumber,
                    code: phoneNumberString,
                    email: email,
                },
            },
        };

        axios
            .get('/local/ajax/mobileId.php', requestBody)
            .then((response) => {
                clearTimeout(hideTimeout);
                ajaxForms.showFormMessages(phoneConfirmModal);
                hideTimeout = setTimeout(() => ajaxForms.hideFormMessages(phoneConfirmModal), 5000);
                if (response.data.success) {
                    ajaxForms.showSuccessMessage(phoneConfirmModal, response.data.message);
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
            })
            .catch((error) => {
                console.error('Ошибка:', error);
                ajaxForms.showFailureMessage(phoneConfirmModal, 'Произошла ошибка при отправке формы');
            });
    }

    // Collapses logic

    const addressBlock = document.querySelector('.js-o-address-block');
    const discountsBlock = document.querySelector('.js-o-discounts-block');
    const paymentBlock = document.querySelector('.js-o-payment-block');

    // personal info
    const personalInfoForm = personalInfoBlock?.querySelector<HTMLFormElement>('.js-o-form');
    const personalInfoSubmit = personalInfoBlock?.querySelector<HTMLElement>('.js-o-submit-btn');
    const personalInfoCollapse = personalInfoBlock?.querySelector<HTMLElement>('.js-o-block-collapse');
    const personalInfoEntered = personalInfoBlock?.querySelector('.js-o-block-entered');

    const personalInfoEnterButton = personalInfoBlock?.querySelector('.js-o-block-enter');
    const personalInfoEditButton = personalInfoBlock?.querySelector('.js-o-edit-btn');

    // address
    const addressForm = addressBlock?.querySelector<HTMLFormElement>('.js-o-form');
    const addressSubmit = addressBlock?.querySelector<HTMLElement>('.js-o-submit-btn');
    const addressCollapse = addressBlock?.querySelector<HTMLElement>('.js-o-block-collapse');
    const addressEntered = addressBlock?.querySelector('.js-o-block-entered');

    const addressEditButton = addressBlock?.querySelector('.js-o-edit-btn');

    // discounts
    const discountsForm = discountsBlock?.querySelector<HTMLFormElement>('.js-o-form');
    const discountsSubmit = discountsBlock?.querySelector<HTMLElement>('.js-o-submit-btn');
    const discountsCollapse = discountsBlock?.querySelector<HTMLElement>('.js-o-block-collapse');
    const discountsEntered = discountsBlock?.querySelector('.js-o-block-entered');

    const discountsEditButton = discountsBlock?.querySelector('.js-o-edit-btn');

    // payment
    const paymentForm = paymentBlock?.querySelector<HTMLFormElement>('.js-o-form');
    const paymentSubmit = paymentBlock?.querySelector<HTMLElement>('.js-o-submit-btn');
    const paymentCollapse = paymentBlock?.querySelector<HTMLElement>('.js-o-block-collapse');

    const nameInputGroup = personalInfoBlock?.querySelector('.js-name-input-group');
    const phoneInputGroup = personalInfoBlock?.querySelector('.js-phone-input-group');

    const addressValueInputGroup = addressBlock?.querySelector('.js-address-value-input-group');
    const addressCommentInputGroup = addressBlock?.querySelector('.js-address-comment-input-group');

    if (personalInfoForm) {
        const personalInfoValidator = createValidator(personalInfoForm, {
            scrollToInvalidInputOptions: {
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            },
        });

        // personal info
        const handlePersonalInfoFormSubmit = () => {
            const isPersonalInfoFormValid = personalInfoValidator.validate();

            if (isPersonalInfoFormValid) {
                const namePreview = document.querySelector('.js-personal-info-name-preview');
                const nameInput = nameInputGroup?.querySelector<HTMLInputElement>('.js-input');
                const phonePreview = document.querySelector('.js-personal-info-phone-preview');
                const phoneInput = phoneInputGroup?.querySelector<HTMLInputElement>('.js-input');
                const emailPreview = document.querySelector('.js-personal-info-email-preview');
                const emailInput = emailInputGroup?.querySelector<HTMLInputElement>('.js-input');

                if (namePreview) {
                    namePreview.innerHTML = nameInput?.value ?? '';
                }

                if (phonePreview) {
                    phonePreview.innerHTML = phoneInput?.value ?? '';
                }

                if (emailPreview) {
                    emailPreview.innerHTML = emailInput?.value ?? '';
                }

                personalInfoBlock?.classList.add('filled');
                personalInfoEnterButton?.classList.remove('active');
                personalInfoEditButton?.classList.add('active');

                if (personalInfoCollapse) {
                    gsap.set(personalInfoCollapse, { height: personalInfoEntered?.scrollHeight });
                }

                if (addressBlock?.classList.contains('not-touched')) {
                    addressBlock?.classList.remove('not-touched');

                    if (addressCollapse) {
                        gsap.set(addressCollapse, { height: addressCollapse?.scrollHeight });
                    }
                }
            }
        };

        pageHandlers.set(personalInfoSubmit, handlePersonalInfoFormSubmit);
    }

    const handlePersonalInfoEdit = () => {
        if (personalInfoCollapse) {
            gsap.set(personalInfoCollapse, { height: personalInfoCollapse?.scrollHeight });
        }

        personalInfoBlock?.classList.remove('filled');
        personalInfoEditButton?.classList.remove('active');
    };

    pageHandlers.set(personalInfoEditButton, handlePersonalInfoEdit);

    if (addressForm) {
        const addressInfoValidator = createValidator(addressForm, {
            scrollToInvalidInputOptions: {
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            },
        });

        // address
        const handleAddressFormSubmit = () => {
            const isAddressFormValid = addressInfoValidator.validate();

            if (isAddressFormValid) {
                const addressValuePreview = document.querySelector('.js-address-value-preview');
                const addressValueInput = addressValueInputGroup?.querySelector<HTMLInputElement>('.js-input');
                const addressCommentPreview = document.querySelector('.js-address-comment-preview');
                const addressCommentInput = addressCommentInputGroup?.querySelector<HTMLInputElement>('.js-input');

                if (addressValuePreview) {
                    addressValuePreview.innerHTML = addressValueInput?.value ?? '';
                }

                if (addressCommentPreview) {
                    addressCommentPreview.innerHTML = addressCommentInput?.value ?? '';
                }

                addressBlock?.classList.add('filled');
                addressEditButton?.classList.add('active');

                if (addressCollapse) {
                    gsap.set(addressCollapse, { height: addressEntered?.scrollHeight });
                }

                if (discountsBlock?.classList.contains('not-touched')) {
                    discountsBlock?.classList.remove('not-touched');

                    if (discountsCollapse) {
                        gsap.set(discountsCollapse, { height: discountsCollapse?.scrollHeight });
                    }
                }
            }
        };

        pageHandlers.set(addressSubmit, handleAddressFormSubmit);
    }

    const handleAddressEdit = () => {
        if (addressCollapse) {
            gsap.set(addressCollapse, { height: addressCollapse?.scrollHeight });
        }

        addressBlock?.classList.remove('filled');
        addressEditButton?.classList.remove('active');
    };

    pageHandlers.set(addressEditButton, handleAddressEdit);

    // discounts
    const handleDiscountsFormSubmit = async () => {
        const shouldChangeCollapse = await handlePromoSubmitButton();
        if (shouldChangeCollapse) {
            discountsBlock?.classList.add('filled');
            discountsEditButton?.classList.add('active');

            if (discountsCollapse) {
                gsap.set(discountsCollapse, { height: discountsEntered?.scrollHeight });
            }

            if (paymentBlock?.classList.contains('not-touched')) {
                paymentBlock?.classList.remove('not-touched');

                if (paymentCollapse) {
                    gsap.set(paymentCollapse, { height: paymentCollapse?.scrollHeight });
                }
            }
        }
    };

    pageHandlers.set(discountsSubmit, handleDiscountsFormSubmit);

    const handleDiscountsEdit = () => {
        if (discountsCollapse) {
            gsap.set(discountsCollapse, { height: discountsCollapse?.scrollHeight });
        }

        discountsBlock?.classList.remove('filled');
        discountsEditButton?.classList.remove('active');
    };

    pageHandlers.set(discountsEditButton, handleDiscountsEdit);

    function generateRandomNumberString(length: number) {
        const characters = '0123456789';
        let result = '';

        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            result += characters.charAt(randomIndex);
        }

        return result;
    }

    const totalPriceElements = document.querySelectorAll<HTMLElement>('.js-total-amount');
    const totalPriceString = totalPriceElements[0];

    // payment
    const handlePaymentFormSubmit = () => {
        const productsContainer = document.querySelector('.js-o-content-collapse');
        const products = productsContainer?.querySelectorAll<HTMLElement>('.js-o-content-product');
        let productsArray: { id: string; price: number; quantity: number }[] = [];
        let productIds: string[] = [];
        let totalPrice = 0;

        products?.forEach((product) => {
            const productId = product.dataset.product;
            const productPriceElement = product.querySelector('.js-price-element');
            const quantityElement = product.querySelector('.js-product-count-value');
            const nameElement = product.querySelector('.js-o-content__product-name');
            const clearPriceString = productPriceElement?.innerHTML.replace(/\s/g, '').replace(/₽/g, '');
            const quantity = Number(quantityElement?.innerHTML);
            const price = Number(clearPriceString);
            const name = nameElement?.innerHTML ?? '';

            productsArray.push({
                id: name,
                price: price,
                quantity: quantity,
            });

            if (productId) {
                productIds.push(productId);
            }
        });

        if (totalPriceString) {
            totalPrice = parseFloat(totalPriceString.replace(/\s/g, ''));
        }

        window._tmr?.push({
            type: 'reachGoal',
            id: '3421145',
            value: totalPrice,
            goal: 'Purchase',
            params: {
                product_id: productIds,
            },
        });

        window.dataLayer?.push({
            ecommerce: {
                currencyCode: 'RUB',
                purchase: {
                    actionField: {
                        id: generateRandomNumberString(8),
                    },
                    products: productsArray,
                },
            },
        });
    };

    pageHandlers.set(paymentSubmit, handlePaymentFormSubmit);

    interface IResponseProductItem {
        id: number;
        previousPrice: string;
        price: string;
    }

    interface IPromoResponseSuccess {
        success: true;
        products: IResponseProductItem[];
        totalDiscount: string;
        total: string;
        bonusesAccrued: string;
    }

    interface IPromoResponseError {
        success: false;
        message: string;
    }

    type TPromoResponse = IPromoResponseSuccess | IPromoResponseError;

    const promoInput = document.querySelector<HTMLInputElement>('.js-promo-input');
    const promoSubmitBtn = document.querySelector<HTMLButtonElement>('.js-promo-submit-btn');
    const promoAppMessage = document.querySelector('.js-promo-app-message');

    const usedPromoValue = document.querySelector('.js-used-promo-value');
    const totalAmount = document.querySelector('.js-total-amount');
    const totalAmounts = document.querySelectorAll('.js-total-amount');
    const totalDiscount = document.querySelector('.js-total-discount');
    const discountItem = document.querySelector('.js-discount-item');
    const totalBonuses = document.querySelector('.js-total-bonuses');
    const priceElements = document.querySelectorAll('.js-price-element');

    const handlePromoSubmitButton = async () => {
        return new Promise(async (resolve) => {
            if (!promoInput || !promoInput.value) {
                resolve(true);
                return;
            }

            try {
                const promoRequest = await axios.post<TPromoResponse>(promoSubmitBtn?.dataset.href ?? '', {
                    value: promoInput?.value,
                });

                if (promoRequest.data.success === true) {
                    if (promoRequest.data.totalDiscount === '-') {
                        openDynamicBanner('Промокод не найден', true);
                        resolve(false);
                        return;
                    }

                    openDynamicBanner('Промокод успешно применен', true);
                    resolve(true);
                    if (usedPromoValue) {
                        usedPromoValue.innerHTML = promoInput?.value.toUpperCase() ?? '';
                    }

                    promoRequest.data.products.forEach(({ id, previousPrice, price }) => {
                        const productElement = document.querySelector(`.js-o-content-product[data-id="${id}"]`);

                        const previousPriceElement =
                            productElement?.querySelector<HTMLElement>('.js-previous-price-element');
                        const priceElement = productElement?.querySelector<HTMLElement>('.js-price-element');

                        if (previousPrice !== price) {
                            if (previousPriceElement) {
                                previousPriceElement.innerHTML = `${previousPrice} ₽`;
                                gsap.fromTo(
                                    previousPriceElement,
                                    { y: 0, scale: 1, opacity: 1 },
                                    { y: -16, scale: 11 / 14, opacity: 0.4, ease: 'power2.easeInOut', duration: 0.5 },
                                );
                            }

                            if (priceElement) {
                                const tl = gsap.timeline({ ease: ease1 });

                                tl.to(priceElement, {
                                    opacity: 0,
                                    duration: 0.5,
                                    onComplete: () => {
                                        priceElement.innerHTML = `${price} ₽`;
                                    },
                                });

                                tl.to(priceElement, {
                                    opacity: 1,
                                    duration: 0.5,
                                });
                            }
                        }
                    });

                    if (totalAmounts) {
                        totalAmounts.forEach((totalAmount) => {
                            const tl = gsap.timeline({ ease: ease1 });

                            tl.to(totalAmount, {
                                opacity: 0,
                                duration: 0.5,
                                onComplete: () => {
                                    if (promoRequest.data.success) {
                                        totalAmount.innerHTML = `${promoRequest.data.total} ₽`;
                                    }
                                },
                            });

                            tl.to(totalAmount, {
                                opacity: 1,
                                duration: 0.5,
                            });
                        });
                    }

                    if (totalDiscount) {
                        const tl = gsap.timeline({ ease: ease1 });

                        tl.to(totalDiscount, {
                            opacity: 0,
                            duration: 0.5,
                            onComplete: () => {
                                if (promoRequest.data.success) {
                                    totalDiscount.innerHTML = `${promoRequest.data.totalDiscount} ₽`;
                                }
                            },
                        });

                        tl.to(totalDiscount, {
                            opacity: 1,
                            duration: 0.5,
                        });
                    }

                    if (totalBonuses) {
                        const tl = gsap.timeline({ ease: ease1 });

                        tl.to(totalBonuses, {
                            opacity: 0,
                            duration: 0.5,
                            onComplete: () => {
                                if (promoRequest.data.success) {
                                    totalBonuses.innerHTML = `${promoRequest.data.bonusesAccrued} Б`;
                                }
                            },
                        });

                        tl.to(totalBonuses, {
                            opacity: 1,
                            duration: 0.5,
                        });
                    }
                } else {
                    resolve(false);
                }
            } catch (e) {
                if (promoAppMessage) {
                    promoAppMessage.innerHTML = 'Ошибка отправки, попробуйте позже';
                }
                promoInput?.parentElement?.classList.add('is-error');
                resolve(false);
            }
        });
    };

    const header = document.querySelector<HTMLElement>('.js-header');

    header?.classList.add('header-state--back-btn');

    const handleScroll = () => {
        if (window.scrollY > 0) {
            header?.classList.remove('header-state--back-btn');
        } else {
            header?.classList.add('header-state--back-btn');
        }
    };

    pageHandlers.set(document, handleScroll, 'scroll');

    const products = document.querySelectorAll<HTMLElement>('.js-o-content-product');
    const bonusesItem = document.querySelector<HTMLElement>('.js-bonus-item');
    const bottomInfo = document.querySelector('.js-o-content__bottom');

    let currentRemovingProductsCount = 0;

    products.forEach((productElement) => {
        const priceElement = productElement.querySelector<HTMLElement>('.js-price-element');
        const priceValue = productElement.dataset.price;

        const removeBtn = productElement.querySelector('.js-o-cart-product-remove-btn');
        const restoreBtn = productElement.querySelector('.js-o-cart-product-restore-btn');

        let priceAmountValue: number;

        if (priceElement && priceElement.textContent) {
            priceAmountValue = parseInt(priceElement.textContent.split(' ').join(''), 10);
        }

        const counterMinus = productElement.querySelector('.js-hp-cart-product-counter-minus');
        const counterValueElement = productElement.querySelector<HTMLElement>('.js-product-count-value');
        const counterPlus = productElement.querySelector('.js-hp-cart-product-counter-plus');
        let productId = -1;
        let dataProduct = -1;

        if (productElement.dataset.id) {
            productId = parseInt(productElement.dataset.id) ?? -1;
        }

        if (productElement.dataset.product) {
            dataProduct = parseInt(productElement.dataset.product) ?? -1;
        }

        if (productId === -1) {
            return;
        }

        let sendingProductsQuantity = 0;
        let debounceTimeout: NodeJS.Timeout;
        let isPreviousRequestNotFinished = false;

        const handleCounterChangeClosure = (action: 'increment' | 'decrement') => () => {
            if (!counterValueElement) {
                return;
            }

            if (action === 'decrement' && counterValueElement?.innerText === '1') {
                return;
            }

            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }

            if (counterValueElement && priceElement && priceAmountValue && priceValue) {
                if (action === 'decrement') {
                    sendingProductsQuantity -= 1;

                    counterValueElement.innerText = `${parseInt(counterValueElement.innerText, 10) - 1}`;

                    priceAmountValue -= +priceValue;
                    priceElement.innerText = `${formatPrice(priceAmountValue)} ₽`;
                } else {
                    sendingProductsQuantity += 1;

                    counterValueElement.innerText = `${parseInt(counterValueElement.innerText, 10) + 1}`;

                    priceAmountValue += +priceValue;
                    priceElement.innerText = `${formatPrice(priceAmountValue)} ₽`;
                }
            }

            debounceTimeout = setTimeout(async () => {
                if (sendingProductsQuantity === 0) {
                    return;
                }

                try {
                    if (isPreviousRequestNotFinished) {
                        axiosAbortController.abort();
                    }

                    isPreviousRequestNotFinished = true;

                    const productAdditionResponse = await axios.post<IProductAdditionResponse>(
                        '/local/ajax/orderUpdate.php',
                        {
                            id: productElement.dataset.product,
                            quantity: sendingProductsQuantity,
                            action: 'update',
                        },
                        {
                            signal: axiosAbortController.signal,
                        },
                    );

                    isPreviousRequestNotFinished = false;
                    if (!productAdditionResponse.data.success) {
                        openDynamicBanner(productAdditionResponse.data.message);
                    } else {
                        sendingProductsQuantity = 0;

                        if (totalAmounts && totalBonuses) {
                            totalAmounts.forEach((totalAmount) => {
                                totalAmount.innerHTML = productAdditionResponse.data.total.toString() + ' ₽';
                                totalBonuses.innerHTML = productAdditionResponse.data.bonusesAccrued + ' Б';
                            });
                        }

                        if (bonusesItem) {
                            bonusesItem.innerHTML = productAdditionResponse.data.bonusesWrittenOff + ' ₽';
                        }

                        if (priceElements) {
                            priceElements.forEach((element, index) => {
                                element.innerHTML = productAdditionResponse.data.products[index].price + ' ₽';
                            });
                        }

                        productAdditionResponse.data.products.forEach(({ id, previousPrice, price }) => {
                            const productElement = document.querySelector(`.js-o-content-product[data-id="${id}"]`);

                            const previousPriceElement =
                                productElement?.querySelector<HTMLElement>('.js-previous-price-element');
                            const priceElement = productElement?.querySelector<HTMLElement>('.js-price-element');
                            if (previousPrice !== price) {
                                if (previousPriceElement) {
                                    previousPriceElement.innerHTML = `${previousPrice} ₽`;
                                    gsap.fromTo(
                                        previousPriceElement,
                                        { y: 0, scale: 1, opacity: 1 },
                                        {
                                            y: -16,
                                            scale: 11 / 14,
                                            opacity: 0.4,
                                            ease: 'power2.easeInOut',
                                            duration: 0.5,
                                        },
                                    );
                                }

                                if (priceElement) {
                                    const tl = gsap.timeline({ ease: ease1 });

                                    tl.to(priceElement, {
                                        opacity: 0,
                                        duration: 0.5,
                                        onComplete: () => {
                                            priceElement.innerHTML = `${price} ₽`;
                                        },
                                    });

                                    tl.to(priceElement, {
                                        opacity: 1,
                                        duration: 0.5,
                                    });
                                }
                            } else {
                                if (previousPriceElement) {
                                    previousPriceElement.innerHTML = '';
                                }
                            }
                        });

                        if (discountItem) {
                            const tl = gsap.timeline({ ease: ease1 });

                            tl.to(discountItem, {
                                opacity: 0,
                                duration: 0.5,
                                onComplete: () => {
                                    if (
                                        productAdditionResponse.data.success &&
                                        productAdditionResponse.data.totalDiscount !== '-'
                                    ) {
                                        discountItem.innerHTML = `
                                            <p class="o-content__info-name">Скидка по промокоду</p>
                                            <p class="o-content__info-value js-total-discount">${productAdditionResponse.data.totalDiscount} ₽</p>
                                        `;
                                    } else {
                                        discountItem.innerHTML = '';
                                    }
                                },
                            });

                            tl.to(discountItem, {
                                opacity: 1,
                                duration: 0.5,
                            });
                        }
                    }
                } catch (error) {
                    sendingProductsQuantity = 0;
                    isPreviousRequestNotFinished = false;

                    if (counterValueElement) {
                        counterValueElement.innerText = `${+counterValueElement.innerText - sendingProductsQuantity}`;
                    }

                    throw error;
                }
            }, 1000);
        };

        const handleProductRemove = () => {
            productElement.classList.add('removing');

            currentRemovingProductsCount += 1;

            productRemoveTimeout = setTimeout(async () => {
                try {
                    const productRemovingResponse = await axios.post<IProductAdditionResponse>(
                        productElement.dataset.href!,
                        {
                            id: dataProduct,
                            action: 'delete',
                        },
                    );

                    if (!productRemovingResponse.data.success) {
                        openDynamicBanner(productRemovingResponse.data.message);
                    } else {
                        const product = productElement.querySelector('.js-o-content__product-name');
                        const productName = product?.innerHTML;
                        currentRemovingProductsCount -= 1;
                        productElement.classList.add('removed');

                        // metric event
                        window.dataLayer?.push({
                            ecommerce: {
                                currencyCode: 'RUB',
                                remove: {
                                    products: [
                                        {
                                            id: productName,
                                        },
                                    ],
                                },
                            },
                        });
                    }

                    if (totalPriceElements && productRemovingResponse.data.data?.totalPrice) {
                        totalPriceElements.forEach((totalPriceString) => {
                            totalPriceString.innerText =
                                formatPrice(productRemovingResponse.data.data.totalPrice) + ' ₽';
                        });
                    }

                    if (totalBonuses && productRemovingResponse.data.data?.bonusesAccrued) {
                        totalBonuses.innerHTML = productRemovingResponse.data.data?.bonusesAccrued + ' Б';
                    }

                    productElement.remove();

                    if (productRemovingResponse.data.data?.totalCount === 0) {
                        bottomInfo?.classList.add('remove-item');
                        openDynamicBanner(errorEmptyCart);

                        setTimeout(() => {
                            window.location.href = '/catalog/';
                        }, 3000);
                    }
                } catch (error) {
                    openDynamicBanner(errorMessage);
                    productElement.classList.remove('removing');

                    currentRemovingProductsCount -= 1;

                    throw error;
                }
            }, 3000);
        };

        const handleProductRestore = () => {
            clearTimeout(productRemoveTimeout);
            productElement.classList.remove('removing');
        };

        let productRemoveTimeout: NodeJS.Timeout;

        productHandlers.set(removeBtn, handleProductRemove);
        productHandlers.set(restoreBtn, handleProductRestore);

        const customEvent = new CustomEvent(`handle-product-count-increment-${productId}`, {
            bubbles: true,
        });

        customEventsMap.set(productId, customEvent);

        const handleMinusClick = handleCounterChangeClosure('decrement');
        const handlePlusClick = handleCounterChangeClosure('increment');

        productHandlers.set(counterMinus, handleMinusClick);
        productHandlers.set(counterPlus, handlePlusClick);
        productHandlers.set(
            document,
            handlePlusClick,
            `handle-product-count-increment-${productId}` as keyof WindowEventMap,
        );
    });
};

const destroy = () => {
    pageHandlers.clear();

    const header = document.querySelector<HTMLElement>('.js-header');
    header?.classList.remove('header-state--back-btn');
};

const _module = { init, destroy };

export default _module;
